// src/pages/JobsPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import API_BASE_URL from '../utils/apiConfig';
import { jobRoles, jobLocations } from '../utils/constants';
import JobCard from '../components/JobCard';
import { injectMixedContent } from '../utils/adInjector';
import AdCard from '../components/AdCard';
import './JobsPage.css';

const JobsPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('private'); // 'private', 'government', 'Following', 'Followers'
    const [jobs, setJobs] = useState([]);
    const [mixedContent, setMixedContent] = useState([]);
    const [loading, setLoading] = useState(true);
    const { isBusiness, isAdmin, firebaseUser } = useAuth(); // Destructured firebaseUser for token extraction

    // State for all filters
    const [filters, setFilters] = useState({
        jobRole: '',
        locationState: '',
        locationDistrict: ''
    });

    const tabsRef = useRef(null);

    const handleTabClick = (tabName, e) => {
        setActiveTab(tabName);
        // Smoothly scrolls the clicked tab button into center focus on mobile screen
        if (e && e.target && tabsRef.current) {
            e.target.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        }
    };

    const fetchJobs = async () => {
        setLoading(true);
        try {
            const params = {
                jobType: activeTab,
                jobRole: filters.jobRole,
                locationState: filters.locationState,
                locationDistrict: filters.locationDistrict,
            };

            const headers = {};
            // Pass authorization headers if querying relationship lists
            if (firebaseUser && (activeTab === 'Following' || activeTab === 'Followers')) {
                const idToken = await firebaseUser.getIdToken(true);
                headers.Authorization = `Bearer ${idToken}`;
            }

            const [jobsResponse, adsResponse] = await Promise.all([
                axios.get(`https://api-5iacy4tfya-uc.a.run.app/jobs`, { params, headers }),
                axios.get(`https://api-5iacy4tfya-uc.a.run.app/ads`)
            ]);

            // Safe robust parser for nested Firestore timestamps / raw ISO formats
            const processedJobs = (jobsResponse.data || []).map(job => {
                let parsedTimestamp = new Date();
                if (job.timestamp) {
                    if (job.timestamp._seconds) {
                        parsedTimestamp = new Date(job.timestamp._seconds * 1000);
                    } else if (job.timestamp.seconds) {
                        parsedTimestamp = new Date(job.timestamp.seconds * 1000);
                    } else {
                        parsedTimestamp = new Date(job.timestamp);
                    }
                }

                let parsedLastDate = null;
                if (job.lastDate) {
                    if (job.lastDate._seconds) {
                        parsedLastDate = new Date(job.lastDate._seconds * 1000);
                    } else if (job.lastDate.seconds) {
                        parsedLastDate = new Date(job.lastDate.seconds * 1000);
                    } else {
                        parsedLastDate = new Date(job.lastDate);
                    }
                }

                return {
                    ...job,
                    timestamp: parsedTimestamp,
                    lastDate: parsedLastDate
                };
            });

            setJobs(processedJobs);
            setMixedContent(injectMixedContent(processedJobs, adsResponse.data || [], 4));
        } catch (error) {
            console.error("Error fetching jobs:", error);
            setJobs([]);
            setMixedContent([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch jobs when the component loads or when the active tab/filters change
    useEffect(() => {
        fetchJobs();
    }, [activeTab, filters]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => {
            const newFilters = { ...prev, [name]: value };
            if (name === "locationState") {
                newFilters.locationDistrict = ''; // Reset district if state changes
            }
            return newFilters;
        });
    };

    const isStateWithDistricts = filters.locationState && jobLocations[filters.locationState]?.length > 0;

    return (
        <div className="jobs-page">
            <header className="jobs-header">
                {(isBusiness || isAdmin) ? (
                    <Link to="/post-a-job" className="post-job-link">
                        <span className="plus-icon">+</span> Post a Job
                    </Link>
                ) : (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        Only Business Accounts can post jobs. 
                        <Link to="/profile" style={{ color: 'blue', marginLeft: '5px' }}>Upgrade here</Link>
                    </div>
                )}
            </header>

            <div className="job-tabs" ref={tabsRef}>
                <button 
                    onClick={(e) => handleTabClick('private', e)} 
                    className={`job-tab-btn ${activeTab === 'private' ? 'active' : ''}`}
                >
                    Job Notifications
                </button>
                <button 
                    onClick={(e) => handleTabClick('government', e)} 
                    className={`job-tab-btn ${activeTab === 'government' ? 'active' : ''}`}
                >
                    Govt Job Notifications
                </button>
                <button 
                    onClick={(e) => handleTabClick('Following', e)} 
                    className={`job-tab-btn ${activeTab === 'Following' ? 'active' : ''}`}
                >
                    Following
                </button>
                <button 
                    onClick={(e) => handleTabClick('Followers', e)} 
                    className={`job-tab-btn ${activeTab === 'Followers' ? 'active' : ''}`}
                >
                    Followers
                </button>
            </div>

            {/* Enable location filters for all non-governmental options (private, following, followers) */}
            {activeTab !== 'government' && (
                <div className="filters-container">
                    <select name="jobRole" value={filters.jobRole} onChange={handleFilterChange}>
                        <option value="">All Job Roles</option>
                        {jobRoles.map(role => <option key={role} value={role}>{role}</option>)}
                    </select>
                    <select name="locationState" value={filters.locationState} onChange={handleFilterChange}>
                        <option value="">All Locations</option>
                        {Object.keys(jobLocations).map(loc => <option key={loc} value={loc}>{loc}</option>)}
                    </select>
                    {isStateWithDistricts && (
                        <select name="locationDistrict" value={filters.locationDistrict} onChange={handleFilterChange}>
                            <option value="">All Districts</option>
                            {jobLocations[filters.locationState].map(dist => <option key={dist} value={dist}>{dist}</option>)}
                        </select>
                    )}
                </div>
            )}

            <div className="job-listings">
                {loading ? (
                    <div className="fullscreen-loader">వార్తలు లోడ్ అవుతున్నాయి... (Loading Jobs...)</div>
                ) : mixedContent.length > 0 ? (
                    mixedContent.map(item => {
                        if (item.isAd) {
                            return <AdCard key={item.adId} adData={item} />;
                        } else {
                            return <JobCard key={item.id} job={item} />;
                        }
                    })
                ) : (
                    <div className="no-data-found">
                        <i className="fas fa-briefcase" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                        <h3>నోటిఫికేషన్లు ఏవీ లేవు</h3>
                        <p>ప్రస్తుతానికి ఎటువంటి ఉద్యోగ ప్రకటనలు లేవు. మళ్ళీ ప్రయత్నించండి.</p>
                        <small>(No job notifications found for the selected criteria.)</small>
                    </div>
                )}
            </div>
        </div>
    );
};

export default JobsPage;